import classNames from "classnames"
import { StoryblokComponent } from "gatsby-source-storyblok"
import React, { Fragment, useState, useEffect, useRef } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

import { ScrollLinksBannerItem } from "./ScrollLinksBannerItem"
import ScrollLinksBannerMobileWrapper from "./ScrollLinksBannerMobileWrapper"
import { Color } from "../../../../constants/V2/color"
import { useElementObserver } from "../../../../hooks/V2/useElementObserver"
import {
  getAccentColor,
  getBackgroundColorClass,
  getTransparentBorderColorClass,
} from "../../../../utils/V2/color"
import { useIsBreakpoint } from "../../../../utils/V2/screen"
import LayoutItem from "../../Layouts/LayoutItem"

import { usePageLayoutState } from "@utils/layout"

type ScrollLinksBannerProps = {
  backgroundColor?: Color
  navigationItems: Storyblok.ScrollLinksBannerItem[]
  mobileGridOrder?: number
}

const ScrollLinksBanner = ({
  backgroundColor = Color.Charcoal,
  navigationItems,
  mobileGridOrder,
  ...props
}: ScrollLinksBannerProps) => {
  const activeTextColor = getAccentColor(backgroundColor)
  const [activeAnchorId, setActiveAnchorId] = useState(
    navigationItems[0]?.anchorId
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())
  const swiperRef = useRef<SwiperType | null>(null)
  const { isVisible, startObserving } = useElementObserver(containerRef)
  const isMobile = useIsBreakpoint("mobile")
  const { calculateStickyFooterHeight } = usePageLayoutState()

  useEffect(() => {
    if (isMobile) {
      startObserving()
    }
  }, [isMobile, startObserving])

  useEffect(() => {
    calculateStickyFooterHeight(75)
    return () => calculateStickyFooterHeight(-75)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const anchorId = entry.target.getAttribute("data-anchor-id")
            if (anchorId) {
              setActiveAnchorId(anchorId)
              break
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "-30% 0px -30% 0px",
        threshold: 0.1,
      }
    )

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current?.observe(section)
      }
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [navigationItems])

  useEffect(() => {
    if (swiperRef.current && activeAnchorId) {
      const activeIndex = navigationItems.findIndex(
        (item) => item.anchorId === activeAnchorId
      )

      const itemsInView =
        window.innerWidth > 450 ? 5 : window.innerWidth > 320 ? 5 : 2

      if (activeIndex !== -1 && activeIndex < navigationItems.length - 1) {
        const targetIndex = Math.min(
          Math.max(navigationItems.length - itemsInView, 0),
          activeIndex
        )
        swiperRef.current.slideTo(targetIndex, 300, false)
      }
    }
  }, [activeAnchorId, navigationItems])

  const registerSection = (anchorId: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current.set(anchorId, element)
    }
  }

  return (
    <LayoutItem
      as="div"
      className={classNames(
        "-mb-lg relative !rounded-t-[18px]",
        getBackgroundColorClass(backgroundColor)
      )}
      mobileGridOrder={mobileGridOrder}
      {...props}
      ref={containerRef}
    >
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          getTransparentBorderColorClass(activeTextColor),
          `sticky top-60 z-[15] flex w-screen flex-col gap-16 px-20 sm:border-b md:top-64 lg:flex-row lg:gap-32`
        )}
      >
        {/* Mobile sticky tabs - swiper menu */}
        <ScrollLinksBannerMobileWrapper isVisible={!!isVisible}>
          <div
            className={classNames(
              getTransparentBorderColorClass(activeTextColor),
              "relative -mx-gutter-xs flex border-b sm:-mx-gutter-sm sm:hidden md:-mx-gutter-md md:py-0 md:pb-10 lg:-mx-gutter-lg xl:-mx-gutter-xl"
            )}
          >
            <Swiper
              className="px-gutter-xs sm:px-gutter-sm md:px-gutter-md lg:px-gutter-lg xl:px-gutter-xl"
              slidesPerView="auto"
              spaceBetween={30}
              slidesOffsetBefore={20}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
            >
              {navigationItems &&
                navigationItems.map((item, index) => (
                  <SwiperSlide
                    key={`scroll-link-banner-mobile-item-${index}`}
                    className={"!w-auto"}
                  >
                    <ScrollLinksBannerItem
                      anchorId={item.anchorId}
                      title={item.title}
                      textColor={activeTextColor}
                      isSelected={item.anchorId === activeAnchorId}
                    />
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </ScrollLinksBannerMobileWrapper>

        {/* Tabs for desktop */}
        <div
          className={
            "z-40 ml-auto hidden gap-[30px] overflow-hidden sm:mr-gutter-sm sm:flex md:mr-gutter-md lg:mr-gutter-lg xl:mr-gutter-xl"
          }
        >
          {navigationItems &&
            navigationItems.map((item, index) => (
              <ScrollLinksBannerItem
                anchorId={item.anchorId}
                key={`scroll-link-banner-desktop-item-${index}`}
                title={item.title}
                textColor={activeTextColor}
                isSelected={item.anchorId === activeAnchorId}
              />
            ))}
        </div>
      </div>
      {navigationItems.map((item, index) => (
        <Fragment key={`scroll-link-banner-item-${index}`}>
          <div
            key={`scroll-link-banner-content-${index}`}
            ref={(el) => {
              registerSection(item.anchorId, el)
            }}
            id={item.anchorId}
            data-anchor-id={item.anchorId}
            className="-mb-[28px] md:-mb-40 lg:-mb-56"
          >
            {item.content.map((content, contentIndex) => (
              <StoryblokComponent
                key={`scroll-link-banner-content-${index}-${contentIndex}`}
                blok={content}
              />
            ))}
          </div>
        </Fragment>
      ))}
    </LayoutItem>
  )
}

export default ScrollLinksBanner
